import { useState } from 'react';
import {
  familyLinkRevoke,
  familyLinks,
  familyRequestDecide,
  familyRequests,
  type ApiClient,
  type ApiError,
  type FamilyLinkRequestRow,
  type FamilyLinksResponse,
  type FamilyRelationship,
} from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';
import { ErrorState } from '../../ui/states';

// Restored from the Flutter console's family access card (family_access_card.dart): the
// requests a family member sent with the resident's access code, approved (with who they
// are to the resident, which sets what alerts they may get) or declined by staff. The V3
// design's Family access block has no control for it; this is drawn in that block's own
// style under the access code, and only when a request is waiting.

const RELATIONSHIPS: [FamilyRelationship, string, string][] = [
  ['poa', 'POA or guardian', 'Receives all safety alerts'],
  ['family', 'Family member', 'Safety and emergency alerts'],
  ['emergency', 'Emergency contact', 'Only 911-level emergencies'],
];

const CAPTION = 'font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:600;margin-top:13px;';
const LINK = 'font-size:16px;font-weight:600;flex:0 1 auto;min-width:0;background:none;border:0;padding:0;cursor:pointer;font-family:inherit;';

export function FamilyRequests({
  client,
  residentId,
  orgId,
  toggles,
  readOnly,
  onDecided,
}: {
  client: ApiClient;
  residentId: string;
  orgId: string | undefined;
  /** What family may see today: an approved member gets exactly these. */
  toggles: Record<string, boolean> | null;
  readOnly: boolean;
  onDecided: () => void;
}) {
  const q = useQuery(
    (signal) => familyRequests(client, { query: { residentId, organizationId: orgId! }, signal }),
    [client, residentId, orgId],
    { enabled: !!orgId },
  );
  const [ask, setAsk] = useState<{ r: FamilyLinkRequestRow; approve: boolean } | null>(null);
  const [rel, setRel] = useState<FamilyRelationship>('family');
  const [err, setErr] = useState<ApiError | null>(null);
  const rows = q.data?.requests ?? [];
  if (!rows.length) return null;
  return (
    <div data-testid="family-requests">
      <div style={sx(CAPTION)}>{`Waiting for your answer · ${rows.length}`}</div>
      {rows.map((r) => (
        <div key={r.id} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:7px 0;font-size:16px;')}>
          <span style={sx('flex:1 1 160px;min-width:0;')}>
            <span style={sx('display:block;font-weight:600;color:#22272E;')}>{r.family_name ?? r.family_email ?? 'A family member'}</span>
            {r.family_email && r.family_name && <span style={sx('display:block;font-size:14px;color:#54606C;')}>{r.family_email}</span>}
          </span>
          <button type="button" disabled={readOnly} onClick={() => { setRel('family'); setAsk({ r, approve: true }); }} style={sx(LINK + 'color:#2F5C97;')}>
            Approve
          </button>
          <button type="button" disabled={readOnly} onClick={() => setAsk({ r, approve: false })} style={sx(LINK + 'color:#54606C;')}>
            Decline
          </button>
        </div>
      ))}
      {err && <ErrorState error={err} />}
      {ask && (
        <ConfirmDialog
          title={ask.approve ? `Give ${ask.r.family_name ?? 'them'} family access?` : `Decline ${ask.r.family_name ?? 'this request'}?`}
          body={
            ask.approve ? (
              <>
                <span style={sx('display:block;')}>They see only what family access has switched on. Who are they to the resident?</span>
                <span role="radiogroup" aria-label="Relationship" style={sx('display:flex;flex-direction:column;gap:6px;margin-top:10px;')}>
                  {RELATIONSHIPS.map(([k, label, sub]) => (
                    <label key={k} style={sx('display:flex;align-items:baseline;gap:8px;font-size:16px;color:#22272E;cursor:pointer;')}>
                      <input type="radio" name="relationship" checked={rel === k} onChange={() => setRel(k)} />
                      <span>
                        <b style={sx('font-weight:700;')}>{label}</b>
                        <span style={sx('color:#54606C;')}>{' · ' + sub}</span>
                      </span>
                    </label>
                  ))}
                </span>
              </>
            ) : (
              'They are not linked and see nothing. They can ask again with a new code.'
            )
          }
          confirmLabel={ask.approve ? 'Approve' : 'Decline'}
          danger={!ask.approve}
          onConfirm={async () => {
            setErr(null);
            await familyRequestDecide(client, {
              params: { id: ask.r.id },
              body: ask.approve ? { organizationId: orgId!, approve: true, relationship: rel, toggles: toggles ?? {} } : { organizationId: orgId!, approve: false },
            });
            q.reload();
            onDecided();
          }}
          onClose={() => setAsk(null)}
        />
      )}
    </div>
  );
}

const SCOPE_WORD: Record<string, string> = { all: 'all safety alerts', safety: 'safety and emergency alerts', emergency: 'only 911-level emergencies' };
const REL_WORD: Record<string, string> = { poa: 'POA', guardian: 'Guardian', family: 'Family member', emergency: 'Emergency contact' };

/**
 * Linked family and what alerts each may get, with Revoke (the Flutter family access
 * card's list, restored): revoking stops their alerts and stamps the consent revoked;
 * the portal link stays. Drawn only when someone is linked.
 */
export function FamilyLinks({ client, residentId, orgId, readOnly }: { client: ApiClient; residentId: string; orgId: string | undefined; readOnly: boolean }) {
  const q = useQuery((signal) => familyLinks(client, { params: { id: residentId }, query: { organizationId: orgId! }, signal }), [client, residentId, orgId], { enabled: !!orgId });
  const [ask, setAsk] = useState<FamilyLinksResponse['links'][number] | null>(null);
  const rows = q.data?.links ?? [];
  if (!rows.length) return null;
  return (
    <div data-testid="family-links">
      <div style={sx(CAPTION)}>{`Linked family · ${rows.length}`}</div>
      {rows.map((l) => (
        <div key={l.familyUserId} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:7px 0;font-size:16px;')}>
          <span style={sx('flex:1 1 160px;min-width:0;')}>
            <span style={sx('display:block;font-weight:600;color:#22272E;')}>{l.name ?? l.email ?? 'A family member'}</span>
            <span style={sx('display:block;font-size:14px;color:' + (l.revoked ? '#B3261E' : '#54606C') + ';')}>
              {l.revoked
                ? 'Alerts revoked · portal access kept'
                : [l.relationship ? REL_WORD[l.relationship] ?? l.relationship : null, l.scope ? SCOPE_WORD[l.scope] ?? l.scope : 'every alert they are eligible for'].filter(Boolean).join(' · ')}
            </span>
          </span>
          {!l.revoked && (
            <button type="button" disabled={readOnly} onClick={() => setAsk(l)} style={sx(LINK + 'color:#B3261E;')}>
              Revoke alerts
            </button>
          )}
        </div>
      ))}
      {ask && (
        <ConfirmDialog
          title={`Stop alerts to ${ask.name ?? ask.email ?? 'them'}?`}
          body="They are no longer paged about the resident. They keep portal access; a new approved request restores alerts."
          confirmLabel="Revoke"
          danger
          onConfirm={async () => {
            await familyLinkRevoke(client, { params: { familyUserId: ask.familyUserId }, body: { organizationId: orgId!, residentId } });
            q.reload();
          }}
          onClose={() => setAsk(null)}
        />
      )}
    </div>
  );
}
